import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, BellOff } from 'lucide-react';
import {
  can,
  REMINDER_LEAD_LABELS,
  REMINDER_LEADS,
  TEAM_TIME_ZONES,
  type ReminderLead,
  type SessionReminder,
  type SessionReminderInput,
  type TeamDetail,
  type TeamReminder,
  type TeamReminderInput,
} from '@hoopify/shared';
import { api, errorMessage } from '../../lib/api';
import { useMe } from '../../lib/session';
import { Alert, Button, Dialog, Section } from '../ui';

const zoneLabel = (id: string) => TEAM_TIME_ZONES.find((z) => z.id === id)?.label.split(/[,(]/)[0]!.trim() ?? id;
const leadLabel = (n: ReminderLead) => REMINDER_LEAD_LABELS[n];

function LeadSelect({ value, onChange, disabled, id }: { value: ReminderLead; onChange: (n: ReminderLead) => void; disabled?: boolean; id: string }) {
  return (
    <select id={id} className="field-input w-auto py-1.5 text-[14px]" value={value} disabled={disabled} onChange={(e) => onChange(Number(e.target.value) as ReminderLead)}>
      {REMINDER_LEADS.map((n) => (
        <option key={n} value={n}>
          {leadLabel(n)}
        </option>
      ))}
    </select>
  );
}

/** Your reminder setting for this team. Every member chooses for themselves. */
export function TeamReminderSettings({ team }: { team: TeamDetail }) {
  const me = useMe();
  const qc = useQueryClient();
  const key = ['reminder', team.id];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<TeamReminder>(`/teams/${team.id}/reminders/me`) });
  const save = useMutation({
    mutationFn: (v: TeamReminderInput) => api.put<TeamReminder>(`/teams/${team.id}/reminders/me`, v),
    onSuccess: (r) => {
      qc.setQueryData(key, r);
      void qc.invalidateQueries({ queryKey: ['reminder', team.id, 'session'] });
    },
  });
  const coach = can(team.role, 'sessions:details');
  const r = save.isPending && save.variables ? { ...q.data!, ...save.variables } : q.data;

  return (
    <Section
      title="Session reminders"
      description={
        coach
          ? 'An email before each session with the plan, anything still to sort out, and a link to courtside mode.'
          : 'An email before each session with the plan and what to bring. Only for sessions the coach has published.'
      }
    >
      {!r ? (
        q.isError ? <Alert>{errorMessage(q.error)}</Alert> : null
      ) : (
        <div className="max-w-[560px] space-y-3">
          <label className="flex cursor-pointer items-start gap-3">
            <input type="checkbox" className="mt-1 size-4 accent-[var(--action)]" checked={r.enabled} onChange={(e) => save.mutate({ enabled: e.target.checked, leadMinutes: r.leadMinutes })} />
            <span>
              <span className="block font-medium">Email me a reminder before each {team.name} session</span>
              <span className="block text-[13px] text-muted">Sent to {me.email}. You can change or turn off any one session from its page.</span>
            </span>
          </label>
          <div className="flex flex-wrap items-center gap-2 pl-7">
            <label htmlFor="reminder-lead" className="text-[14px] text-muted">
              When
            </label>
            <LeadSelect id="reminder-lead" value={r.leadMinutes} disabled={!r.enabled} onChange={(n) => save.mutate({ enabled: r.enabled, leadMinutes: n })} />
            {save.isSuccess && !save.isPending && <span className="text-[14px] text-ok">Saved</span>}
          </div>
          <p className="pl-7 text-[13px] text-muted">
            Times are {zoneLabel(r.timeZone)} time.{can(team.role, 'team:manage') ? ' Change the team’s time zone in Team details below.' : ''}
          </p>
          {save.isError && <Alert>{errorMessage(save.error)}</Alert>}
        </div>
      )}
    </Section>
  );
}

/** "Reminder: 2 hours before · Change" under a session's heading. */
export function SessionReminderLine({ teamId, sessionId }: { teamId: string; sessionId: string }) {
  const qc = useQueryClient();
  const key = ['reminder', teamId, 'session', sessionId];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<SessionReminder>(`/teams/${teamId}/sessions/${sessionId}/reminder`) });
  const [open, setOpen] = useState(false);
  const r = q.data;
  if (!r) return null;

  const sendsAt = r.sendsAt ? new Date(r.sendsAt) : null;
  const past = sendsAt ? sendsAt.getTime() < Date.now() : false;
  let text: string;
  if (!r.enabled) text = r.mode === 'off' ? 'Reminder off for this session' : 'Reminders are off for this team';
  else if (r.sent) text = 'Reminder sent';
  else if (r.waitingForPublish) text = `Reminder ${leadLabel(r.leadMinutes).toLowerCase()}, once the coach publishes it`;
  else if (past) text = 'Too late for a reminder';
  else text = `Reminder ${leadLabel(r.leadMinutes).toLowerCase()}${r.mode === 'custom' ? ' (just this session)' : ''}`;

  return (
    <>
      <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-[14px] text-muted">
        {r.enabled ? <Bell className="size-3.5" aria-hidden /> : <BellOff className="size-3.5" aria-hidden />}
        <span>{text}</span>
        <span aria-hidden>·</span>
        <button className="font-medium text-action hover:underline" onClick={() => setOpen(true)}>
          Change
        </button>
      </p>
      <SessionReminderDialog open={open} onClose={() => setOpen(false)} r={r} teamId={teamId} sessionId={sessionId} onSaved={(n) => qc.setQueryData(key, n)} />
    </>
  );
}

function SessionReminderDialog({ open, onClose, r, teamId, sessionId, onSaved }: { open: boolean; onClose: () => void; r: SessionReminder; teamId: string; sessionId: string; onSaved: (r: SessionReminder) => void }) {
  const [mode, setMode] = useState<SessionReminder['mode']>(r.mode);
  const [lead, setLead] = useState<ReminderLead>(r.mode === 'custom' ? r.leadMinutes : r.team.leadMinutes);
  const save = useMutation({
    mutationFn: () => api.put<SessionReminder>(`/teams/${teamId}/sessions/${sessionId}/reminder`, (mode === 'custom' ? { mode, leadMinutes: lead } : { mode }) satisfies SessionReminderInput),
    onSuccess: (n) => {
      onSaved(n);
      onClose();
    },
  });
  const teamText = r.team.enabled ? leadLabel(r.team.leadMinutes).toLowerCase() : 'off';
  const option = (value: SessionReminder['mode'], label: React.ReactNode, hint?: string) => (
    <label className="flex cursor-pointer items-start gap-3 rounded-md px-2 py-2 hover:bg-sunken">
      <input type="radio" name="reminder-mode" className="mt-1 size-4 accent-[var(--action)]" checked={mode === value} onChange={() => setMode(value)} />
      <span>
        <span className="block">{label}</span>
        {hint && <span className="block text-[13px] text-muted">{hint}</span>}
      </span>
    </label>
  );
  return (
    <Dialog open={open} onClose={() => { save.reset(); onClose(); }} title="Reminder for this session">
      <fieldset className="space-y-1">
        <legend className="sr-only">Reminder for this session</legend>
        {option('team', `Same as my team setting (${teamText})`, 'Change that on the team’s Settings tab.')}
        {option('off', 'No reminder for this session')}
        {option(
          'custom',
          <span className="flex flex-wrap items-center gap-2">
            A different time:
            <LeadSelect id="session-lead" value={lead} onChange={(n) => { setLead(n); setMode('custom'); }} />
          </span>,
        )}
      </fieldset>
      {save.isError && <div className="mt-3"><Alert>{errorMessage(save.error)}</Alert></div>}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
        <Link to={`/teams/${teamId}?tab=settings`} className="text-[14px] text-action hover:underline">
          Team reminder settings
        </Link>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/** The team's time zone, in Team details (head coach). */
export function TimeZoneSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor="team-tz" className="block text-[14px] font-medium">
        Time zone
      </label>
      <select id="team-tz" className="field-input" value={value} onChange={(e) => onChange(e.target.value)}>
        {TEAM_TIME_ZONES.map((z) => (
          <option key={z.id} value={z.id}>
            {z.label}
          </option>
        ))}
      </select>
      <p className="text-[13px] text-muted">Where the team trains. Reminders go at the right local time.</p>
    </div>
  );
}
